import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

export type CachedImage = {
  originalUrl: string;
  base64: string;
  image: HTMLImageElement;
};

export function useImagesFastCache(
  imageUrls: Array<string | undefined> = []
): Array<CachedImage | undefined> {
  const uniqueImageUrls = useMemo(() => [...new Set(imageUrls)], [imageUrls]);

  const queries = useQueries({
    queries: uniqueImageUrls.map(createImageQuery)
  });

  return useMemo(
    () =>
      imageUrls.map(url => {
        const query = queries.find(q => q.data?.originalUrl === url);

        if (!url || !query?.data) {
          return undefined;
        }

        return createImageResult(url, query.data.base64, query.data.image);
      }),
    [imageUrls, queries]
  );
}

export function useImagesWarmFastCache() {
  const queryClient = useQueryClient();

  const warmCache = useCallback(
    async (imageUrls: Array<string | undefined> = []): Promise<Array<CachedImage | undefined>> => {
      const uniqueImageUrls = [...new Set(imageUrls)];

      // Fetch all images in parallel
      const results = await Promise.allSettled(
        uniqueImageUrls.map(url => queryClient.fetchQuery(createImageQuery(url)))
      );

      // Map results back to original order
      return imageUrls.map(url => {
        const uniqueIndex = uniqueImageUrls.indexOf(url);
        const result = results[uniqueIndex];

        if (result?.status === "fulfilled" && url && result.value) {
          return createImageResult(url, result.value.base64, result.value.image);
        }

        return undefined;
      });
    },
    [queryClient]
  );

  return {
    warmCache
  };
}

function createImageQuery(url: string | undefined) {
  const isDataUrl = url?.startsWith("data:");

  return {
    enabled: !!url,
    queryKey: ["images-fast-cache", url],
    retry: isDataUrl ? 0 : 10,
    retryDelay: isDataUrl ? 0 : 1000,
    queryFn: async (): Promise<CachedImage | undefined> => {
      if (!url) return undefined;

      // Single request to fetch the image with proper Accept header for format optimization
      const response = await fetch(url, {
        headers: {
          Accept: "image/avif,image/webp,image/apng,image/jpeg,image/png,*/*"
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();

      // Convert blob to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (typeof reader.result === "string") {
            resolve(reader.result);
          } else {
            reject(new Error("Failed to convert image to base64"));
          }
        };
        reader.onerror = () => reject(new Error("FileReader error"));
        reader.readAsDataURL(blob);
      });

      // Create Image object using the base64 string
      const image = new window.Image();

      return new Promise<CachedImage | undefined>((resolve, reject) => {
        image.onload = () => {
          resolve(createImageResult(url, base64, image));
        };
        image.onerror = () => {
          reject(new Error("Failed to load image"));
        };
        image.src = base64;
      });
    }
  };
}

function createImageResult(
  originalUrl: string,
  base64: string,
  image: HTMLImageElement
): CachedImage {
  return { originalUrl, base64, image };
}
